import React, { SetStateAction, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { IParams } from '../../../../../../../model/shared/IParams';
import { UploadImage } from '../../../../../../../shared/components/UploadImage';
import { multipartFormData } from '../../../../../../../utils';
import { MEASURE_URL } from '../../../../../../../utils/endpoints';
import { ErrorToast } from '../../../../../../../utils/toasters';
import { IFeedbackData } from './CampaignFeedback';
import { useMediaQuery } from '@mui/material';
import { BootstrapBreakpoints } from '../../../../../../../enum/BootstrapBreakpoints';
import TooltipOverlay from '../../../../../../../shared/components/tooltip/Tooltip';

interface ICampaignFeedbackForm {
    feedbackData: IFeedbackData;
    setFeedbackData: (...args: any) => void;
    setIsFormOpened: React.Dispatch<SetStateAction<boolean>>;
}

const CampaignFeedbackForm = ({ setIsFormOpened, feedbackData, setFeedbackData }: ICampaignFeedbackForm) => {
    const { id } = useParams<keyof IParams>();
    const { t } = useTranslation();
    const [files, setFiles] = useState<Blob[] | MediaSource[] | undefined>();
    const isMobile = useMediaQuery(`(max-width: ${BootstrapBreakpoints.MD}px)`);

    const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFeedbackData((prev: any) => ({ ...prev, [name]: value }));
    };

    const submitHandler = async () => {
        if (id) {
            await multipartFormData(files, `${MEASURE_URL}/feedback/${id}`, JSON.stringify(feedbackData))
                .then(response => {
                    setIsFormOpened(false);
                    setFeedbackData(response.data);
                    setFiles(undefined);
                })
                .catch(err => ErrorToast(err));
        }
    };

    return (
        <Row>
            <Col md={6} className="mb-3 mb-md-0 px-0">
                <UploadImage
                    saveFile={setFiles}
                    classes="d-flex"
                    classRow="col ml-2"
                    classCard="px-3"
                    previewImage={feedbackData?.feedbackFile}
                    acceptedFormats="application/pdf, application/vnd.openxmlformats-officedocument.presentationml.presentation"
                    isDeleteHidden={true}
                    maxSize={104857600} // 100MB
                />
            </Col>
            <Col md={6} className="mt-5 mt-md-0">
                <Form.Control
                    onChange={changeHandler}
                    as="textarea"
                    value={feedbackData?.teamFeedback ?? ''}
                    name="teamFeedback"
                    rows={isMobile ? 5 : 10}
                    maxLength={2000}
                    placeholder={t('influencer.offer_management.modal.add_comment')}
                />
            </Col>
            <Col md={12} className="d-flex justify-content-end mt-2 mt-md-0">
                <Button variant="outline-primary" className="mr-2" onClick={() => setIsFormOpened(false)}>
                    {t('general.button.cancel')}
                </Button>
                <TooltipOverlay
                    content={
                        (!files && !feedbackData?.feedbackFile[0]) || !feedbackData?.teamFeedback ? (
                            <>{t('brand.campaign.measure.feedbackMessage')}</>
                        ) : null
                    }
                >
                    <Button
                        disabled={(!files && !feedbackData?.feedbackFile[0]) || !feedbackData?.teamFeedback}
                        onClick={submitHandler}
                    >
                        {t('general.button.addFeedback')}
                    </Button>
                </TooltipOverlay>
            </Col>
        </Row>
    );
};

export default CampaignFeedbackForm;
